import { Link } from "react-router-dom";
import { Smartphone, Tablet, Gamepad2, Recycle, Laptop, Building2, Zap, BadgeCheck, PoundSterling, ArrowRight, MapPin, MessageCircle, PackageOpen, Wrench, PackageCheck } from "lucide-react";
import { SHOP } from "@/lib/api";
import Reviews from "@/components/Reviews";
import Seo from "@/components/Seo";

const SERVICES = [
  { icon: Smartphone, title: "Phone Repairs", desc: "Screens, batteries, charging ports, cameras and more — most makes and models.", img: "https://images.pexels.com/photos/288479/pexels-photo-288479.jpeg?auto=compress&w=900", wide: true },
  { icon: Tablet, title: "Tablet Repairs", desc: "iPads and Android tablets brought back to life.", img: "https://images.pexels.com/photos/1334597/pexels-photo-1334597.jpeg?auto=compress&w=900" },
  { icon: Gamepad2, title: "Games Console Repairs", desc: "HDMI ports, overheating, drive faults and controllers.", img: "https://images.pexels.com/photos/1298601/pexels-photo-1298601.jpeg?auto=compress&w=900" },
  { icon: Laptop, title: "Laptop Repairs", desc: "Screens, keyboards, batteries and data recovery.", img: "https://images.pexels.com/photos/2148217/pexels-photo-2148217.jpeg?auto=compress&w=900" },
  { icon: Building2, title: "Business Devices", desc: "Several devices to keep running? We look after small business fleets.", img: "https://images.pexels.com/photos/251225/pexels-photo-251225.jpeg?auto=compress&w=900" },
  { icon: Recycle, title: "Recycling & Disposal", desc: "Secure disposal and recycling of IT equipment — desktops, laptops and mobiles.", img: "https://images.pexels.com/photos/19037726/pexels-photo-19037726.jpeg?auto=compress&w=900", wide: true },
];

const STEPS = [
  { icon: PackageOpen, title: "1. Book online", desc: "Create an account, tell us the device and the fault. Takes two minutes." },
  { icon: Wrench, title: "2. Drop off or post it", desc: "Bring it to us in Hyde, or post it from anywhere in the UK." },
  { icon: PackageCheck, title: "3. Fixed & returned", desc: "Fast turnaround — many repairs same-day — and every job carries a warranty. Post-in devices come back with free next-day return delivery." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo title="Phone, Tablet & Console Repair in Hyde, Tameside | Appleman Tech Repair" description="Independent phone, tablet, laptop and games console repair in Hyde, Tameside. Fast turnaround, honest pricing, warranty on every repair. Book online." />
      {/* HERO */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-accent mb-6" data-testid="hero-eyebrow">Hyde · Tameside · Post-in repairs UK-wide</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-heading">
              Broken device?<br />
              <span className="text-accent" style={{ WebkitTextStroke: "2px #0A0A0A" }}>Fixed fast.</span><br />
              Done right.
            </h1>
            <p className="mt-8 text-lg text-neutral-600 max-w-xl leading-relaxed" data-testid="hero-copy">
              Independent repair for phones, tablets, laptops and games consoles. Same-day turnaround on most jobs, fair and honest pricing, and a warranty on every repair.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-8 py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
                Book a repair <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/sell" data-testid="hero-sell-btn"
                className="inline-flex items-center gap-2 border-2 border-black bg-accent px-8 py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
                Sell my device
              </Link>
            </div>
            <p className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-neutral-500" data-testid="hero-booking-only">
              Repairs by online booking only — book before you visit
            </p>
            <Link to="/buy" className="mt-3 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest border-b-2 border-black pb-1 hover:gap-4 transition-[gap]" data-testid="hero-buy-link">
              Or buy a refurbished device <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="border-2 border-black shadow-hard-lg overflow-hidden" data-testid="hero-image-wrap">
              <img src="https://images.pexels.com/photos/6755075/pexels-photo-6755075.jpeg?auto=compress&w=1200" alt="Technician repairing a smartphone" className="img-ink w-full h-[420px] object-cover" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-accent border-2 border-black px-5 py-3 shadow-hard font-display font-bold uppercase text-sm tracking-wider" data-testid="hero-badge">
              Warranty on every repair
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="border-b-2 border-black bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
          <p className="eyebrow text-neutral-500 mb-4">Why Appleman</p>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-12" data-testid="why-heading">No nonsense. Just fixed.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: "Fast turnaround", desc: "Same-day fixes on most common repairs. We know your phone is your lifeline." },
              { icon: PoundSterling, title: "Fair, honest pricing", desc: "Straight quotes before we start. If it isn't worth fixing, we'll tell you." },
              { icon: BadgeCheck, title: "Warranty on every repair", desc: "Every job leaves the shop with a warranty. If it fails, we put it right." },
            ].map((f, i) => (
              <div key={f.title} className="bg-white border-2 border-black p-8 shadow-hard brutal-hover" data-testid={`why-card-${i}`}>
                <f.icon className="h-10 w-10 mb-6 text-accent" strokeWidth={2.5} />
                <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-3">{f.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="eyebrow text-neutral-500 mb-4">What we fix</p>
              <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl" data-testid="services-heading">Repairs & services</h2>
            </div>
            <Link to="/book" className="inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider border-b-2 border-black pb-1 hover:gap-4 transition-[gap]" data-testid="services-book-link">
              Book yours <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="border-2 border-black bg-white shadow-hard brutal-hover overflow-hidden" data-testid={`service-card-${i}`}>
                {s.img && <img src={s.img} alt={s.title} className="img-ink w-full h-48 object-cover border-b-2 border-black" />}
                <div className="p-8">
                  <s.icon className="h-8 w-8 mb-4" strokeWidth={2.5} />
                  <h3 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">{s.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b-2 border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
          <p className="eyebrow text-accent mb-4">How it works</p>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-12" data-testid="how-heading">Three steps. Sorted.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.title} className="border-2 border-white p-8" data-testid={`how-step-${i}`}>
                <s.icon className="h-10 w-10 mb-6 text-accent" strokeWidth={2} />
                <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-3">{s.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="border-2 border-black shadow-hard-lg overflow-hidden order-2 lg:order-1">
            <img src="https://images.pexels.com/photos/19037726/pexels-photo-19037726.jpeg?auto=compress&w=1000" alt="Old phones stacked ready for recycling" className="img-ink w-full h-80 object-cover" data-testid="community-image" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="eyebrow text-accent mb-4">Community</p>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-6" data-testid="community-heading">Repairing more than devices</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We partner with <strong>Tameside Community Computers CIC</strong>, repairing donated smartphones and tablets free of charge so they reach people living in digital poverty.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Got old tech? We also handle secure disposal and recycling of IT equipment — desktops, laptops and mobile devices — so nothing ends up in landfill.
            </p>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <Reviews />

      {/* CTA BAND */}
      <section className="bg-accent border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl" data-testid="cta-heading">Got a device to sell?</h2>
            <p className="mt-3 text-lg max-w-xl">We buy used and faulty phones, tablets and consoles. Tell us what you've got and we'll come back with a fair offer.</p>
          </div>
          <Link to="/sell" data-testid="cta-sell-btn"
            className="inline-flex items-center gap-2 border-2 border-black bg-white px-8 py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover shrink-0">
            Get an offer <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* VISIT */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow text-neutral-500 mb-4">Find us</p>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl mb-6" data-testid="visit-heading">Visit us in Hyde</h2>
            <p className="flex gap-3 text-neutral-600 mb-3"><MapPin className="h-5 w-5 shrink-0 text-accent" /> {SHOP.address}</p>
            <p className="text-neutral-600 mb-8">Repairs are by online booking only. Open Monday to Friday, 10:30am – 6:00pm. Post-in repairs accepted from anywhere in the UK.</p>
            <a href={SHOP.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="visit-whatsapp-btn"
              className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
              <MessageCircle className="h-5 w-5" /> WhatsApp {SHOP.whatsappDisplay}
            </a>
          </div>
          <div className="border-2 border-black shadow-hard overflow-hidden bg-neutral-50" data-testid="visit-map-card">
            <iframe
              title="Map to Appleman Tech Repair, 24 Springwell Gardens, Hyde"
              src="https://maps.google.com/maps?q=24%20Springwell%20Gardens%2C%20Hyde%20SK14%203QL&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[320px] grayscale contrast-105"
              loading="lazy"
              data-testid="visit-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
